import { type FC, useState, useEffect } from "react";
import JsonView from "@uiw/react-json-view";
import { lightTheme, brighterDarkTheme } from "@/components/ui/jsonViewTheme";

type JsonViewerProps = {
  fileId: string;
};

export const JsonViewer: FC<JsonViewerProps> = ({ fileId }) => {
  const [data, setData] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    fetch(`/api/file/${fileId}`, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load file: ${res.statusText}`);
        return res.text();
      })
      .then((text) => {
        setData(JSON.parse(text));
        setLoading(false);
      })
      .catch((err) => {
        if ((err as Error).name === "AbortError") return;
        if (err instanceof SyntaxError) {
          setError("Invalid JSON");
        } else {
          setError(err instanceof Error ? err.message : "Failed to load file");
        }
        setLoading(false);
      });

    return () => controller.abort();
  }, [fileId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-sm text-red-500">{error}</p>
      </div>
    );
  }

  const sharedProps = {
    value: data as object,
    collapsed: false as const,
    enableClipboard: true,
    displayDataTypes: false,
    displayObjectSize: true,
    shortenTextAfterLength: 100,
  };

  return (
    <div className="h-full w-full overflow-auto p-4">
      {/* Light mode */}
      <div className="dark:hidden">
        <JsonView
          {...sharedProps}
          style={{
            ...lightTheme,
            backgroundColor: "transparent",
            fontSize: "12px",
          } as React.CSSProperties}
        />
      </div>
      {/* Dark mode */}
      <div className="hidden dark:block">
        <JsonView
          {...sharedProps}
          style={{
            ...brighterDarkTheme,
            backgroundColor: "transparent",
            fontSize: "12px",
          } as React.CSSProperties}
        />
      </div>
    </div>
  );
};
